// ...imports (unchanged)
import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import CollapseHeader from '../../../../core/common/collapse-header';
import { checkPermission } from '../../../../core/permissons/CheckPermissons';
import { all_routes } from '../../../router/all_routes';
import apiDefinitions from '../../../../core/api/apiDefinitions';
import KeyAllocationTable from '../Key Dashboard/KeyAllocationTable';
import KeyRequestTable from '../Key Dashboard/KeyRequestTable';
import ApprovedKeyRequestTable from '../Key Dashboard/ApprovedKeyRequestTable';
import VehicleKeySuggest from './VehicleKeySuggest';
import { Modal, Button, Table, Form, Spinner } from 'react-bootstrap';
import Swal from 'sweetalert2';

// --- types (unchanged) ---
type NavItem = { label: string; icon: string; link: string; permission?: string; };

interface KeyAllocation {
  id: number;
  keyId: number;
  keyCode: string;
  handoverTo: string;
  returnedBy: string | null;
  handoverBy: string;
  isRestricted: boolean;
  reason: string | null;
  returnTo: string | null;
  status: string;
  handoverType: 'PERMANENT' | 'TEMPORARY' | null;
  handoverDate: string;
  returnDate: string | null;
}

interface KeyRequest {
  id: number;
  keyIds: number[];
  reason: string;
  requestedBy: string;
  requestedDate: string;
  approvalStatus: string; // PENDING | APPROVED | HANDOVER | RETURNED | REJECTED ...
  approvedBy: string | null;
}

interface VehicleKeyBooking {
  bookingId: number;
  vehicleRegistrationNumber: string;
  vehicleType: string;
  keyCodes: string[];
  keyIds: string[];
  departureTime: string;
  driverName: string;
  driverEmpNo: string;
}

interface ApiResponse<T> {
  status: string;
  statusDescription: string;
  data?: T[];
  total?: number | null;
}

// --- constants (unchanged) ---
const statusPermissions: { [key: string]: string } = {
  RETURNED: 'ROLE_GET_KEY_ALLOCATIONS',
  LOST: 'ROLE_GET_KEY_ALLOCATIONS',
  HANDOVER: 'ROLE_GET_KEY_ALLOCATIONS',
  PERMANENT: 'ROLE_GET_KEY_ALLOCATIONS',
  NOT_RETURNED: 'ROLE_GET_KEY_ALLOCATIONS',
  PENDING: 'ROLE_PENDING_VEHICLE_BOOKING',
  APPROVED: 'ROLE_GET_RECURRING_VEHICLE_BOOKING_REQUESTS',
  VEHICLE_KEY_REQUESTS: 'ROLE_VEHICLE_HANDOVER',
};

const statusColors: { [key: string]: string } = {
  RETURNED: 'badge bg-success',
  LOST: 'badge bg-danger',
  HANDOVER: 'badge bg-info',
  PERMANENT: 'badge bg-primary',
  NOT_RETURNED: 'badge bg-warning',
  PENDING: 'badge bg-warning',
  APPROVED: 'badge bg-success',
  VEHICLE_KEY_REQUESTS: 'badge bg-info',
};

const statusCardStyles: {
  [key: string]: { bgColor: string; iconColor: string; icon: string; className: string };
} = {
  RETURNED: { bgColor: '#d1fae5', iconColor: '#10b981', icon: 'fas fa-check-circle', className: 'bg-green-100' },
  LOST: { bgColor: '#fee2e2', iconColor: '#ef4444', icon: 'fas fa-times-circle', className: 'bg-red-100' },
  HANDOVER: { bgColor: '#e0f2fe', iconColor: '#3b82f6', icon: 'fas fa-key', className: 'bg-blue-100' },
  PERMANENT: { bgColor: '#dbeafe', iconColor: '#2563eb', icon: 'fas fa-lock', className: 'bg-blue-100' },
  NOT_RETURNED: { bgColor: '#fef3c7', iconColor: '#f59e0b', icon: 'fas fa-clock', className: 'bg-amber-100' },
  PENDING: { bgColor: '#fef3c7', iconColor: '#f59e0b', icon: 'fas fa-hourglass-half', className: 'bg-amber-100' },
  APPROVED: { bgColor: '#d1fae5', iconColor: '#10b981', icon: 'fas fa-check-circle', className: 'bg-green-100' },
  VEHICLE_KEY_REQUESTS: { bgColor: '#e0f2fe', iconColor: '#3b82f6', icon: 'fas fa-car', className: 'bg-blue-100' },
};

const statusLabels: { [key: string]: string } = {
  RETURNED: 'Returned Keys',
  LOST: 'Lost Keys',
  HANDOVER: 'Handover Keys',
  PERMANENT: 'Permanent Keys',
  NOT_RETURNED: 'Not Returned Keys',
  PENDING: 'Pending Approvals',
  APPROVED: 'Approved Keys',
  VEHICLE_KEY_REQUESTS: 'Vehicle Key Requests',
};

const keyReqStatusBadgeClass: Record<string, string> = {
  PENDING: 'badge bg-warning',
  APPROVED: 'badge bg-success',
  HANDOVER: 'badge bg-info',
  RETURNED: 'badge bg-primary',
  REJECTED: 'badge bg-danger',
};

const KeyDashboard: React.FC = () => {
  // --- existing state (unchanged) ---
  const [returnedAllocations, setReturnedAllocations] = useState<KeyAllocation[]>([]);
  const [lostAllocations, setLostAllocations] = useState<KeyAllocation[]>([]);
  const [handoverAllocations, setHandoverAllocations] = useState<KeyAllocation[]>([]);
  const [pendingRequests, setPendingRequests] = useState<KeyRequest[]>([]);
  const [approvedRequests, setApprovedRequests] = useState<KeyRequest[]>([]);
  const [vehicleKeyBookings, setVehicleKeyBookings] = useState<VehicleKeyBooking[]>([]);

  const [isLoading, setIsLoading] = useState<{ [key: string]: boolean }>({
    RETURNED: false,
    LOST: false,
    HANDOVER: false,
    PENDING: false,
    APPROVED: false,
    VEHICLE_KEY_REQUESTS: false,
  });

  const [currentDateTime, setCurrentDateTime] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [modalData, setModalData] = useState<KeyAllocation[] | KeyRequest[] | VehicleKeyBooking[]>([]);

  // --- My Key Requests (unchanged) ---
  const [myKeyRequests, setMyKeyRequests] = useState<KeyRequest[]>([]);
  const [loadingMyKeyReqs, setLoadingMyKeyReqs] = useState<boolean>(false);
  const [errorMyKeyReqs, setErrorMyKeyReqs] = useState<string | null>(null);
  const [keyReqPage, setKeyReqPage] = useState<number>(1);
  const [keyReqTotal, setKeyReqTotal] = useState<number | null>(null);
  const [keyReqDateFilter, setKeyReqDateFilter] = useState<string>('');
  const KEY_REQ_PAGE_SIZE = 15;

  const isOk = (res: any) => res?.status === 200 && (res?.data?.status === 'SUCCESS' || res?.data?.status === 'S1000');

  const formatDate = (iso?: string | null) => {
    if (!iso) return 'N/A';
    const d = new Date(iso);
    return isNaN(d.getTime())
      ? 'N/A'
      : d.toLocaleString('en-US', {
          timeZone: 'Asia/Colombo',
          year: 'numeric',
          month: 'short',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          hour12: true,
        });
  };

  const fetchMyKeyRequests = useCallback(
    async (page: number) => {
      if (!checkPermission('ROLE_LOGIN')) {
        setErrorMyKeyReqs('You do not have permission to view key requests.');
        return;
      }
      setLoadingMyKeyReqs(true);
      setErrorMyKeyReqs(null);
      try {
        const payload = { filterType: '', filterValue: '', date: keyReqDateFilter || '' };
        const res = await apiDefinitions.getUserCreatedKeyRequests(page.toString(), payload);
        if (isOk(res)) {
          const rows: KeyRequest[] = Array.isArray(res.data.data) ? res.data.data : [];
          setMyKeyRequests(rows);
          setKeyReqTotal(typeof res.data.total === 'number' ? res.data.total : null);
        } else {
          setErrorMyKeyReqs(res?.data?.statusDescription || 'Failed to fetch key requests.');
          setMyKeyRequests([]);
        }
      } catch (err: any) {
        const msg = err.response?.data?.statusDescription || err.message;
        setErrorMyKeyReqs(`Failed to fetch key requests: ${msg}`);
        setMyKeyRequests([]);
      } finally {
        setLoadingMyKeyReqs(false);
      }
    },
    [keyReqDateFilter]
  );

  // --- fetchers (unchanged) ---
  const fetchAllocations = useCallback(
    async (status: 'RETURNED' | 'LOST' | 'HANDOVER', filter: { filterType: string; filterValue: string }) => {
      if (!checkPermission('ROLE_GET_KEY_ALLOCATIONS')) {
        setIsLoading((prev) => ({ ...prev, [status]: false }));
        return;
      }
      setIsLoading((prev) => ({ ...prev, [status]: true }));
      try {
        let page = 1;
        const all: KeyAllocation[] = [];
        while (true) {
          const res = await apiDefinitions.getAllHandoverKeys(page, filter);
          const responseData = res.data as ApiResponse<KeyAllocation>;
          if (responseData.status !== 'S1000') break;
          const batch = Array.isArray(responseData.data) ? responseData.data : [];
          if (batch.length === 0) break;
          all.push(...batch);
          page += 1;
          if (page > 1000) break;
        }
        if (status === 'RETURNED') setReturnedAllocations(all);
        if (status === 'LOST') setLostAllocations(all);
        if (status === 'HANDOVER') setHandoverAllocations(all);
      } catch {
        if (status === 'RETURNED') setReturnedAllocations([]);
        if (status === 'LOST') setLostAllocations([]);
        if (status === 'HANDOVER') setHandoverAllocations([]);
      } finally {
        setIsLoading((prev) => ({ ...prev, [status]: false }));
      }
    },
    []
  );

  const fetchPendingRequests = useCallback(async () => {
    const status = 'PENDING';
    if (!checkPermission(statusPermissions[status])) {
      setIsLoading((prev) => ({ ...prev, [status]: false }));
      return;
    }
    setIsLoading((prev) => ({ ...prev, [status]: true }));
    try {
      let page = 1;
      const all: KeyRequest[] = [];
      while (true) {
        const res = await apiDefinitions.getPendingApprovalKeyRequests(page.toString());
        const responseData = res.data as ApiResponse<KeyRequest>;
        if (responseData.status !== 'S1000') break;
        const batch = Array.isArray(responseData.data) ? responseData.data : [];
        if (batch.length === 0) break;
        all.push(...batch);
        page += 1;
        if (page > 1000) break;
      }
      setPendingRequests(all);
    } catch {
      setPendingRequests([]);
    } finally {
      setIsLoading((prev) => ({ ...prev, PENDING: false }));
    }
  }, []);

  const fetchApprovedRequests = useCallback(async () => {
    const status = 'APPROVED';
    if (!checkPermission(statusPermissions[status])) {
      setIsLoading((prev) => ({ ...prev, [status]: false }));
      return;
    }
    setIsLoading((prev) => ({ ...prev, [status]: true }));
    try {
      let page = 1;
      const all: KeyRequest[] = [];
      const payload = { filterType: 'STATUS', filterValue: 'APPROVED' };
      while (true) {
        const res = await apiDefinitions.getAllKeyRequests(page.toString(), payload);
        const responseData = res.data as ApiResponse<KeyRequest>;
        if (responseData.status !== 'S1000') break;
        const batch = Array.isArray(responseData.data) ? responseData.data : [];
        if (batch.length === 0) break;
        all.push(...batch);
        page += 1;
        if (page > 1000) break;
      }
      setApprovedRequests(all);
    } catch {
      setApprovedRequests([]);
    } finally {
      setIsLoading((prev) => ({ ...prev, APPROVED: false }));
    }
  }, []);

  const fetchVehicleKeyBookings = useCallback(async () => {
    const status = 'VEHICLE_KEY_REQUESTS';
    if (!checkPermission(statusPermissions[status])) {
      setIsLoading((prev) => ({ ...prev, [status]: false }));
      return;
    }
    setIsLoading((prev) => ({ ...prev, [status]: true }));
    try {
      const res = await apiDefinitions.getApprovedVehicleBookingsWithKeys();
      const responseData = res.data as ApiResponse<VehicleKeyBooking>;
      if (responseData.status !== 'S1000') {
        setVehicleKeyBookings([]);
        return;
      }
      const data = Array.isArray(responseData.data) ? responseData.data : [];
      setVehicleKeyBookings(data);
    } catch {
      setVehicleKeyBookings([]);
    } finally {
      setIsLoading((prev) => ({ ...prev, VEHICLE_KEY_REQUESTS: false }));
    }
  }, []);

  // --- clock + initial loads (unchanged) ---
  useEffect(() => {
    const update = () => {
      setCurrentDateTime(
        new Date().toLocaleString('en-US', {
          timeZone: 'Asia/Colombo',
          hour12: true,
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })
      );
    };
    update();
    const timer = setInterval(update, 60_000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    fetchAllocations('RETURNED', { filterType: 'status', filterValue: 'RETURNED' });
    fetchAllocations('LOST', { filterType: 'status', filterValue: 'LOST' });
    fetchAllocations('HANDOVER', { filterType: 'status', filterValue: 'HANDOVER' });
    fetchPendingRequests();
    fetchApprovedRequests();
    fetchVehicleKeyBookings();
  }, [fetchAllocations, fetchPendingRequests, fetchApprovedRequests, fetchVehicleKeyBookings]);

  useEffect(() => {
    fetchMyKeyRequests(keyReqPage);
  }, [fetchMyKeyRequests, keyReqPage, keyReqDateFilter]);

  const isToday = (dateStr: string) => {
    const d = new Date(dateStr);
    const now = new Date();
    return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
  };

  const counts = {
    RETURNED: returnedAllocations.length,
    LOST: lostAllocations.length,
    HANDOVER: handoverAllocations.filter((a) => a.handoverType === 'TEMPORARY' && a.status === 'HANDOVER').length,
    PERMANENT: handoverAllocations.filter((a) => a.handoverType === 'PERMANENT' && a.status === 'HANDOVER').length,
    NOT_RETURNED: handoverAllocations.filter(
      (a) => a.handoverType === 'TEMPORARY' && a.status === 'HANDOVER' && !isToday(a.handoverDate)
    ).length,
    PENDING: pendingRequests.length,
    APPROVED: approvedRequests.length,
    VEHICLE_KEY_REQUESTS: vehicleKeyBookings.length,
  };

  const handleCardClick = (category: string) => {
    let data: KeyAllocation[] | KeyRequest[] | VehicleKeyBooking[] = [];
    switch (category) {
      case 'RETURNED': data = returnedAllocations; break;
      case 'LOST': data = lostAllocations; break;
      case 'HANDOVER': data = handoverAllocations.filter((a) => a.handoverType === 'TEMPORARY' && a.status === 'HANDOVER'); break;
      case 'PERMANENT': data = handoverAllocations.filter((a) => a.handoverType === 'PERMANENT' && a.status === 'HANDOVER'); break;
      case 'NOT_RETURNED':
        data = handoverAllocations.filter((a) => a.handoverType === 'TEMPORARY' && a.status === 'HANDOVER' && !isToday(a.handoverDate));
        break;
      case 'PENDING': data = pendingRequests; break;
      case 'APPROVED': data = approvedRequests; break;
      case 'VEHICLE_KEY_REQUESTS': data = vehicleKeyBookings; break;
      default: break;
    }
    setModalData(data);
    setSelectedCategory(category);
    setShowModal(true);
  };

  // existing view for allocations (left as-is)
  const handleViewClick = async (_id: number) => {
    if (!checkPermission('ROLE_GET_KEY_ALLOCATIONS')) {
      Swal.fire({ icon: 'error', title: 'Permission Denied', text: 'You do not have permission to view key details.', confirmButtonText: 'OK' });
      return;
    }
  };

  // ===== NEW: "View Key Request" flow =====
  const [showViewReqModal, setShowViewReqModal] = useState(false);
  const [viewReqLoading, setViewReqLoading] = useState(false);
  const [viewReqError, setViewReqError] = useState<string | null>(null);
  const [viewReq, setViewReq] = useState<KeyRequest | null>(null);

  const handleViewKeyRequest = async (id: number) => {
    if (!checkPermission('ROLE_GET_KEY_REQUESTS')) {
      Swal.fire({ icon: 'error', title: 'Permission Denied', text: 'You do not have permission to view key requests.', confirmButtonText: 'OK' });
      return;
    }
    setViewReqLoading(true);
    setViewReqError(null);
    setViewReq(null);
    try {
      const res = await apiDefinitions.getKeyRequestById(id);
      if (res?.data?.status === 'S1000' && res.data.data) {
        setViewReq(res.data.data as KeyRequest);
        setShowViewReqModal(true);
      } else {
        setViewReqError(res?.data?.statusDescription || 'Failed to fetch key request.');
      }
    } catch (e: any) {
      setViewReqError(e?.response?.data?.statusDescription || e.message || 'Failed to fetch key request.');
    } finally {
      setViewReqLoading(false);
    }
  };
  const closeViewReqModal = () => {
    setShowViewReqModal(false);
    setViewReq(null);
    setViewReqError(null);
  };
  // ===== /NEW =====

  // My Key Requests handlers (unchanged)
  const handleKeyReqDateFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setKeyReqDateFilter(e.target.value);
    setKeyReqPage(1);
  };

  const keyReqTotalPages = keyReqTotal ? Math.max(1, Math.ceil(keyReqTotal / KEY_REQ_PAGE_SIZE)) : null;

  const handleKeyReqPageChange = (dir: 'prev' | 'next') => {
    if (keyReqTotalPages) {
      if (dir === 'prev' && keyReqPage > 1) setKeyReqPage((p) => p - 1);
      if (dir === 'next' && keyReqPage < keyReqTotalPages) setKeyReqPage((p) => p + 1);
    } else {
      if (dir === 'prev' && keyReqPage > 1) setKeyReqPage((p) => p - 1);
      if (dir === 'next' && myKeyRequests.length === KEY_REQ_PAGE_SIZE) setKeyReqPage((p) => p + 1);
    }
  };

  const renderKeyIds = (ids: number[]) => (ids?.length ? ids.join(', ') : '—');

  // ---------- refreshAll + closeAndRefresh from your previous version ----------
  const refreshAll = useCallback(async () => {
    await Promise.all([
      fetchAllocations('RETURNED', { filterType: 'status', filterValue: 'RETURNED' }),
      fetchAllocations('LOST', { filterType: 'status', filterValue: 'LOST' }),
      fetchAllocations('HANDOVER', { filterType: 'status', filterValue: 'HANDOVER' }),
      fetchPendingRequests(),
      fetchApprovedRequests(),
      fetchVehicleKeyBookings(),
      fetchMyKeyRequests(keyReqPage),
    ]);
  }, [fetchAllocations, fetchPendingRequests, fetchApprovedRequests, fetchVehicleKeyBookings, fetchMyKeyRequests, keyReqPage]);

  const closeAndRefresh = useCallback(async () => {
    setShowModal(false);
    setSelectedCategory(null);
    setModalData([]);
    await refreshAll();
  }, [refreshAll]);

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedCategory(null);
    setModalData([]);
  };
  // ---------------------------------------------------------------------------

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="row">
          <div className="col-md-12">
            {/* header */}
            <div className="page-header">
              <div className="row align-items-center">
                <div className="col-8"><h4 className="page-title">Key Management/Key Management Dashboard</h4></div>
                <div className="col-4 text-end"><div className="head-icons"><CollapseHeader /></div></div>
              </div>
            </div>

            {Object.values(isLoading).some((loading) => loading) && (
              <div className="text-center py-4">
                <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div>
              </div>
            )}

            {/* stat cards (unchanged) */}
            <div className="row mt-4" style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap' }}>
              {(['RETURNED', 'LOST', 'HANDOVER', 'PERMANENT', 'NOT_RETURNED', 'PENDING', 'APPROVED', 'VEHICLE_KEY_REQUESTS'] as const).map(
                (status) =>
                  checkPermission(statusPermissions[status]) ? (
                    <div className="col-xl-3 col-sm-6 col-12 mb-4" style={{ padding: '0 15px' }} key={status}>
                      <div
                        className="stat-card bg-white rounded-xl shadow-md overflow-hidden"
                        style={{ transition: 'all 0.3s ease', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', cursor: 'pointer' }}
                        onMouseOver={(e) => { e.currentTarget.style.transform = 'translateY(-5px)'; e.currentTarget.style.boxShadow = '0 10px 20px rgba(0,0,0,0.1)'; }}
                        onMouseOut={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)'; }}
                        onClick={() => handleCardClick(status)}
                      >
                        <div className="p-6" style={{ padding: '1.5rem' }}>
                          <div className="flex items-center justify-between mb-4" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div className={`icon-container ${statusCardStyles[status]?.className || 'bg-gray-100'}`}
                              style={{ height: '60px', width: '60px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '12px', backgroundColor: statusCardStyles[status]?.bgColor || '#f3f4f6' }}>
                              <i className={statusCardStyles[status]?.icon || 'fas fa-info-circle'} style={{ fontSize: '1.5rem', color: statusCardStyles[status]?.iconColor || '#6b7280' }} />
                            </div>
                            <span style={{ fontSize: '1.875rem', fontWeight: '700', color: '#1f2937' }}>{counts[status] || 0}</span>
                          </div>
                          <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#374151', margin: 0 }}>{statusLabels[status] || status}</h3>
                        </div>
                      </div>
                    </div>
                  ) : null
              )}
            </div>

            {/* === My Key Requests === */}
            <div className="row mt-4">
              <div className="col-md-12">
                <div className="card">
                  <div className="card-header d-flex justify-content-between align-items-center">
                    <h5 className="card-title mb-0">My Key Requests</h5>
                    <small className="text-muted">{currentDateTime}</small>
                  </div>
                  <div className="card-body">
                    <div className="row mb-3">
                      <div className="col-md-6">
                        <Form.Group>
                          <Form.Label>Filter by Date</Form.Label>
                          <Form.Control type="date" value={keyReqDateFilter} onChange={handleKeyReqDateFilterChange} disabled={loadingMyKeyReqs} />
                        </Form.Group>
                      </div>
                    </div>

                    {loadingMyKeyReqs && <div className="d-flex align-items-center gap-2"><Spinner animation="border" size="sm" /><span>Loading...</span></div>}

                    {!loadingMyKeyReqs && errorMyKeyReqs && (<div className="alert alert-danger" role="alert">{errorMyKeyReqs}</div>)}

                    {!loadingMyKeyReqs && !errorMyKeyReqs && myKeyRequests.length === 0 && (
                      <div className="alert alert-info" role="alert">No key requests found.</div>
                    )}

                    {!loadingMyKeyReqs && !errorMyKeyReqs && myKeyRequests.length > 0 && (
                      <div className="table-responsive">
                        <Table className="table text-nowrap">
                          <thead className="table-primary">
                            <tr>
                              <th>ID</th>
                              <th>Reason</th>
                              <th>Key IDs</th>
                              <th>Requested Date</th>
                              <th>Status</th>
                              <th>Approved By</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {myKeyRequests.map((req) => (
                              <tr key={req.id}>
                                <td>{req.id}</td>
                                <td>{req.reason || '—'}</td>
                                <td>{renderKeyIds(req.keyIds as number[])}</td>
                                <td>{formatDate(req.requestedDate)}</td>
                                <td>
                                  <span className={keyReqStatusBadgeClass[req.approvalStatus] || 'badge bg-secondary'}>
                                    {req.approvalStatus}
                                  </span>
                                </td>
                                <td>{req.approvedBy ?? '—'}</td>
                                <td>
                                  <div className="hstack gap-2">
                                    {/* UPDATED: View now calls our API and opens details modal */}
                                    <Button
                                      variant="icon btn-secondary btn-sm"
                                      title="View Request"
                                      onClick={() => handleViewKeyRequest(req.id)}
                                    >
                                      <i className="ti ti-eye" />
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>

                        <div className="d-flex justify-content-between mt-3">
                          <Button variant="outline-primary" disabled={loadingMyKeyReqs || keyReqPage === 1} onClick={() => handleKeyReqPageChange('prev')}>
                            Previous
                          </Button>
                          <span>Page {keyReqPage}{keyReqTotalPages ? ` of ${keyReqTotalPages}` : ''}</span>
                          <Button
                            variant="outline-primary"
                            disabled={loadingMyKeyReqs || (keyReqTotalPages ? keyReqPage >= keyReqTotalPages : myKeyRequests.length < KEY_REQ_PAGE_SIZE)}
                            onClick={() => handleKeyReqPageChange('next')}
                          >
                            Next
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            {/* === /My Key Requests === */}

            {/* Main modal that opens from stat cards */}
            <Modal show={showModal} onHide={handleCloseModal} size="xl" centered>
              <Modal.Header closeButton>
                <Modal.Title>{selectedCategory ? statusLabels[selectedCategory] : 'Key Allocations'}</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                {selectedCategory === 'PENDING' ? (
                  <KeyRequestTable
                    data={modalData as KeyRequest[]}
                    onActionSuccess={closeAndRefresh}
                  />
                ) : selectedCategory === 'APPROVED' ? (
                  <ApprovedKeyRequestTable
                    data={modalData as KeyRequest[]}
                    onActionSuccess={closeAndRefresh}
                  />
                ) : selectedCategory === 'VEHICLE_KEY_REQUESTS' ? (
                  <VehicleKeySuggest
                    data={modalData as VehicleKeyBooking[]}
                    onHandoverSuccess={closeAndRefresh}
                  />
                ) : (
                  <KeyAllocationTable
                    data={modalData as KeyAllocation[]}
                    statusColors={statusColors}
                    handleViewClick={handleViewClick}
                    onMarkSuccess={closeAndRefresh}
                  />
                )}
              </Modal.Body>
              <Modal.Footer>
                <Button variant="primary" onClick={closeAndRefresh}>Close</Button>
              </Modal.Footer>
            </Modal>

            {/* ===== NEW: View Key Request Modal ===== */}
            <Modal show={showViewReqModal} onHide={closeViewReqModal} centered>
              <Modal.Header closeButton>
                <Modal.Title>View Key Request</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                {viewReqLoading && (
                  <div className="d-flex align-items-center gap-2">
                    <Spinner animation="border" size="sm" />
                    <span>Loading...</span>
                  </div>
                )}

                {!viewReqLoading && viewReqError && (
                  <div className="alert alert-danger" role="alert">
                    {viewReqError}
                  </div>
                )}

                {!viewReqLoading && !viewReqError && viewReq && (
                  <div className="mb-2">
                    <p><strong>ID:</strong> {viewReq.id}</p>
                    <p><strong>Key IDs:</strong> {viewReq.keyIds?.join(', ') || '—'}</p>
                    <p><strong>Reason:</strong> {viewReq.reason || '—'}</p>
                    <p><strong>Requested By:</strong> {viewReq.requestedBy || '—'}</p>
                    <p><strong>Requested Date:</strong> {formatDate(viewReq.requestedDate)}</p>
                    <p>
                      <strong>Status:</strong>{' '}
                      <span className={keyReqStatusBadgeClass[viewReq.approvalStatus] || 'badge bg-secondary'}>
                        {viewReq.approvalStatus}
                      </span>
                    </p>
                    <p><strong>Approved By:</strong> {viewReq.approvedBy || '—'}</p>
                  </div>
                )}
              </Modal.Body>
              <Modal.Footer>
                <Button variant="primary" onClick={closeViewReqModal}>Close</Button>
              </Modal.Footer>
            </Modal>
            {/* ===== /NEW ===== */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default KeyDashboard;
